/*!
 * Store Locator Widget
 * Self-hostable Google Maps-powered store locator.
 *
 * National build: if a visitor's location/search has no stores inside the
 * selected radius, the list falls back to the `fallbackCount` (default 5)
 * closest stores with a notice, instead of an empty "no stores" panel.
 *
 * Usage:
 *   <div id="store-locator"></div>
 *   <script src="store-locator.js"></script>
 *   <script>
 *     StoreLocator.init({
 *       container: '#store-locator',
 *       googleMapsApiKey: 'YOUR_KEY',
 *       storesUrl: 'https://your-host.example.com/stores.json',
 *
 *       // Centering: defaultCenter is the initial view. If autoLocate is true,
 *       // the widget will silently try the user's geolocation and re-center on
 *       // success — falling back to defaultCenter on denial / timeout.
 *       defaultCenter: { lat: 40.7128, lng: -74.0060 },  // optional
 *       defaultZoom: 11,                                 // optional
 *       autoLocate: true,                                // optional
 *
 *       // Marker icon. Either a single image URL applied to every pin, or an
 *       // object keyed by store type ('cash-carry' / 'on-premise' / 'featured'
 *       // / 'default'). Each entry may also be { url, size: [w,h], anchor: [x,y] }.
 *       // If omitted, built-in colored SVG markers are used.
 *       markerIcon: './mapIcon-1.png',                   // optional
 *
 *       theme: { primary: '#10b981' }                    // optional
 *     });
 *   </script>
 */
(function (global) {
  'use strict';

  // ---------- defaults ----------
  const DEFAULT_THEME = {
    primary: '#10b981',
    primaryText: '#ffffff',
    text: '#0f172a',
    muted: '#64748b',
    background: '#ffffff',
    panel: '#ffffff',
    border: '#e2e8f0',
    accent: '#0f172a',
    radius: '8px',
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  };

  const DISTANCE_OPTIONS = [
    { value: 5, label: '5 MI' },
    { value: 10, label: '10 MI' },
    { value: 25, label: '25 MI' },
    { value: 50, label: '50 MI' },
    { value: 0, label: 'NATIONWIDE' },
  ];

  const STORE_TYPES = {
    'cash-carry': { label: 'Cash & Carry', color: '#10b981' },
    'on-premise': { label: 'On-Premise', color: '#0f172a' },
  };

  const US_CENTER = { lat: 39.8283, lng: -98.5795 };
  const US_ZOOM = 4;

  // ---------- script loader ----------
  const loaded = {};
  function loadScript(src, globalCheck) {
    if (globalCheck && globalCheck()) return Promise.resolve();
    if (loaded[src]) return loaded[src];
    loaded[src] = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.defer = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('Failed to load ' + src));
      document.head.appendChild(s);
    });
    return loaded[src];
  }

  function loadGoogleMaps(apiKey) {
    // If Maps is already fully loaded, resolve immediately
    if (global.google && global.google.maps && typeof global.google.maps.Map === 'function') {
      return Promise.resolve();
    }
    if (loaded['_gmaps']) return loaded['_gmaps'];

    // Use the callback parameter — this is the officially recommended loading
    // method and guarantees the API is fully initialized when called.
    // Removed: 'marker' library (not needed — code uses legacy google.maps.Marker)
    // Removed: 'v=weekly' (unstable; default stable channel is safer)
    loaded['_gmaps'] = new Promise((resolve, reject) => {
      const cbName = '__slMapsReady_' + Math.random().toString(36).slice(2, 7);
      global[cbName] = function () {
        delete global[cbName];
        resolve();
      };
      var s = document.createElement('script');
      s.src =
        'https://maps.googleapis.com/maps/api/js?key=' +
        encodeURIComponent(apiKey) +
        '&libraries=places&loading=async&callback=' + cbName;
      s.async = true;
      s.defer = true;
      s.onerror = function () {
        delete global[cbName];
        reject(new Error('Failed to load Google Maps API — check your API key and allowed referrers'));
      };
      document.head.appendChild(s);
    });
    return loaded['_gmaps'];
  }

  function loadMarkerClusterer() {
    return loadScript(
      'https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js',
      () => global.markerClusterer
    );
  }

  // ---------- haversine ----------
  function haversineMiles(a, b) {
    const R = 3958.7613;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const h =
      Math.sin(dLat / 2) ** 2 +
      Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
    return 2 * R * Math.asin(Math.sqrt(h));
  }

  // ---------- open now (optional hours) ----------
  // hours: { mon: "09:00-17:00", tue: "...", ... } or { mon: [["09:00","17:00"]] }
  const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  function isOpenNow(hours, now) {
    if (!hours) return null;
    now = now || new Date();
    const day = DAY_KEYS[now.getDay()];
    const entry = hours[day];
    if (!entry) return false;
    const ranges = Array.isArray(entry)
      ? entry.map((r) => (Array.isArray(r) ? r : String(r).split('-')))
      : [String(entry).split('-')];
    const mins = now.getHours() * 60 + now.getMinutes();
    for (const [openStr, closeStr] of ranges) {
      if (!openStr || !closeStr) continue;
      const [oH, oM] = openStr.split(':').map(Number);
      const [cH, cM] = closeStr.split(':').map(Number);
      const open = oH * 60 + (oM || 0);
      const close = cH * 60 + (cM || 0);
      if (close > open) {
        if (mins >= open && mins < close) return true;
      } else {
        if (mins >= open || mins < close) return true;
      }
    }
    return false;
  }

  // ---------- DOM helpers ----------
  function el(tag, attrs, children) {
    const e = document.createElement(tag);
    if (attrs) {
      for (const k in attrs) {
        if (k === 'style' && typeof attrs[k] === 'object') {
          Object.assign(e.style, attrs[k]);
        } else if (k === 'class') {
          e.className = attrs[k];
        } else if (k.startsWith('on') && typeof attrs[k] === 'function') {
          e.addEventListener(k.slice(2).toLowerCase(), attrs[k]);
        } else if (attrs[k] != null) {
          e.setAttribute(k, attrs[k]);
        }
      }
    }
    if (children != null) {
      const arr = Array.isArray(children) ? children : [children];
      for (const c of arr) {
        if (c == null) continue;
        e.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
      }
    }
    return e;
  }

  // ---------- styles ----------
  function injectStyles(theme, scopeId) {
    if (document.getElementById('sl-styles-' + scopeId)) return;
    const css = `
#${scopeId} { --sl-primary: ${theme.primary}; --sl-primary-text: ${theme.primaryText}; --sl-text: ${theme.text}; --sl-muted: ${theme.muted}; --sl-bg: ${theme.background}; --sl-panel: ${theme.panel}; --sl-border: ${theme.border}; --sl-accent: ${theme.accent}; --sl-radius: ${theme.radius}; font-family: ${theme.fontFamily}; color: var(--sl-text); display: flex; height: 100%; min-height: 500px; background: var(--sl-bg); position: relative; box-sizing: border-box; overflow: hidden; }
#${scopeId} *, #${scopeId} *::before, #${scopeId} *::after { box-sizing: border-box; }
#${scopeId} .sl-panel { width: 380px; max-width: 40%; background: var(--sl-panel); border-right: 1px solid var(--sl-border); display: flex; flex-direction: column; flex-shrink: 0; z-index: 2; height: 100%; min-height: 500px; overflow: hidden; }
#${scopeId} .sl-controls { padding: 16px; border-bottom: 1px solid var(--sl-border); background: var(--sl-panel); position: relative; z-index: 3; flex-shrink: 0; }
#${scopeId} .sl-search-wrap { position: relative; display: flex; align-items: center; background: var(--sl-bg); border: 1px solid var(--sl-border); border-radius: var(--sl-radius); padding: 0 8px; }
#${scopeId} .sl-search-icon, #${scopeId} .sl-geo-btn, #${scopeId} .sl-clear-btn { background: transparent; border: 0; cursor: pointer; padding: 8px; color: var(--sl-muted); display: flex; align-items: center; justify-content: center; }
#${scopeId} .sl-geo-btn:hover, #${scopeId} .sl-clear-btn:hover { color: var(--sl-text); }
#${scopeId} .sl-search-input { flex: 1; border: 0; outline: none; background: transparent; padding: 12px 4px; font-size: 14px; color: var(--sl-text); min-width: 0; }
#${scopeId} .sl-filters { display: flex; gap: 8px; margin-top: 10px; }
#${scopeId} .sl-select { flex: 1; position: relative; }
#${scopeId} .sl-select select { width: 100%; appearance: none; -webkit-appearance: none; background: var(--sl-bg); border: 1px solid var(--sl-border); border-radius: var(--sl-radius); padding: 10px 30px 10px 12px; font-size: 12px; font-weight: 600; letter-spacing: 0.5px; text-transform: uppercase; color: var(--sl-primary); cursor: pointer; }
#${scopeId} .sl-select::after { content: ''; position: absolute; right: 12px; top: 50%; width: 8px; height: 8px; border-right: 2px solid var(--sl-primary); border-bottom: 2px solid var(--sl-primary); transform: translateY(-75%) rotate(45deg); pointer-events: none; }
#${scopeId} .sl-list { flex: 1 1 0; min-height: 0; overflow-y: auto; overflow-x: hidden; -webkit-overflow-scrolling: touch; overscroll-behavior: contain; }
#${scopeId} .sl-item { padding: 18px 16px; border-bottom: 1px solid var(--sl-border); cursor: pointer; transition: background .12s; }
#${scopeId} .sl-item:hover, #${scopeId} .sl-item.active { background: rgba(15,23,42,0.04); }
#${scopeId} .sl-item-name { font-size: 15px; font-weight: 700; color: var(--sl-primary); margin: 0 0 6px; }
#${scopeId} .sl-item-addr { font-size: 13px; color: var(--sl-text); margin: 0 0 10px; line-height: 1.4; }
#${scopeId} .sl-meta { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
#${scopeId} .sl-badge { display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: 999px; font-size: 11px; font-weight: 600; background: rgba(16,185,129,0.12); color: #047857; }
#${scopeId} .sl-badge .sl-dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
#${scopeId} .sl-badge.sl-closed { background: rgba(100,116,139,0.12); color: var(--sl-muted); }
#${scopeId} .sl-distance { display: inline-block; padding: 5px 10px; border-radius: 4px; font-size: 11px; font-weight: 600; background: rgba(15,23,42,0.05); color: var(--sl-text); }
#${scopeId} .sl-directions { display: inline-block; margin-top: 8px; font-size: 12px; font-weight: 600; color: var(--sl-primary); text-decoration: none; }
#${scopeId} .sl-directions:hover { text-decoration: underline; }
#${scopeId} .sl-notice { padding: 12px 16px; font-size: 12px; font-weight: 600; line-height: 1.45; color: var(--sl-text); background: rgba(238,46,83,0.08); border-bottom: 1px solid var(--sl-border); }
#${scopeId} .sl-empty { padding: 32px 16px; text-align: center; color: var(--sl-muted); font-size: 13px; }
#${scopeId} .sl-loading { padding: 24px 16px; text-align: center; color: var(--sl-muted); font-size: 13px; }
#${scopeId} .sl-map { flex: 1; min-height: 100%; position: relative; }
#${scopeId} .sl-map > div { height: 100%; }
#${scopeId} .sl-info { font-family: inherit; max-width: 260px; }
#${scopeId} .sl-info h4 { margin: 0 0 6px; font-size: 14px; color: var(--sl-primary); }
#${scopeId} .sl-info p { margin: 0 0 4px; font-size: 12px; color: var(--sl-text); }
#${scopeId} .sl-info a { color: var(--sl-primary); text-decoration: none; font-size: 12px; }
#${scopeId} .sl-error { padding: 16px; background: #fef2f2; color: #991b1b; font-size: 13px; border: 1px solid #fecaca; border-radius: var(--sl-radius); margin: 16px; }
@media (max-width: 720px) {
  #${scopeId} { flex-direction: column; height: auto; min-height: 600px; }
  #${scopeId} .sl-panel { width: 100%; max-width: 100%; min-height: 0; max-height: 50vh; border-right: 0; border-bottom: 1px solid var(--sl-border); }
  #${scopeId} .sl-map { min-height: 400px; }
}
.pac-container { z-index: 9999 !important; font-family: ${theme.fontFamily}; }
    `;
    const style = el('style', { id: 'sl-styles-' + scopeId }, css);
    document.head.appendChild(style);
  }

  // ---------- marker icon ----------
  // Built-in fallback SVGs (used if no markerIcon is configured).
  function builtInMarkerSvg(type, featured) {
    const cfg = STORE_TYPES[type] || STORE_TYPES['cash-carry'];
    const color = cfg.color;
    if (featured || type === 'on-premise') {
      const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='34' height='40' viewBox='0 0 34 40'><path d='M17 39 L4 26 A8 8 0 0 1 4 14 L4 6 A4 4 0 0 1 8 2 L26 2 A4 4 0 0 1 30 6 L30 14 A8 8 0 0 1 30 26 Z' fill='${color}' stroke='#fff' stroke-width='2'/><path d='M17 9 L19 14 L24 14 L20 17 L21.5 22 L17 19 L12.5 22 L14 17 L10 14 L15 14 Z' fill='#fff'/></svg>`;
      return { url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg), size: [34, 40], anchor: [17, 38] };
    }
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 28 28'><circle cx='14' cy='14' r='11' fill='${color}' stroke='#fff' stroke-width='3'/></svg>`;
    return { url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg), size: [28, 28], anchor: [14, 14] };
  }

  // Resolves the markerIcon config into { url, size, anchor } for a given store.
  // markerIcon config can be:
  //   - undefined/null               → use built-in SVG markers
  //   - string url                   → same icon for every store
  //   - { url, size?, anchor? }      → same icon w/ custom sizing
  //   - { 'cash-carry': <icon>, 'on-premise': <icon>, featured: <icon>, default: <icon> }
  //       where each <icon> is a url string or { url, size?, anchor? }
  function resolveMarkerIcon(config, store) {
    if (!config) return builtInMarkerSvg(store.type, store.featured);

    const pickFromMap = (map) => {
      if (store.featured && map.featured) return map.featured;
      if (store.type && map[store.type]) return map[store.type];
      return map.default || map['cash-carry'] || map['on-premise'] || null;
    };

    let icon;
    if (typeof config === 'string') {
      icon = { url: config };
    } else if (config.url) {
      icon = config;
    } else {
      const picked = pickFromMap(config);
      if (!picked) return builtInMarkerSvg(store.type, store.featured);
      icon = typeof picked === 'string' ? { url: picked } : picked;
    }

    // Defaults: 40×40 with bottom-center anchor (suits teardrop/pin shapes
    // rendered on a square canvas, like the included mapIcon-1.png).
    const size = icon.size || [40, 40];
    const anchor = icon.anchor || [Math.round(size[0] / 2), size[1]];
    return { url: icon.url, size, anchor };
  }

  // ---------- icons (inline SVG strings) ----------
  const ICON_SEARCH = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>`;
  const ICON_CLEAR = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6l12 12M6 18 18 6"/></svg>`;
  const ICON_GEO = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3"/></svg>`;

  // ---------- main widget ----------
  function init(opts) {
    if (!opts || !opts.container) throw new Error('StoreLocator: container required');
    if (!opts.googleMapsApiKey) throw new Error('StoreLocator: googleMapsApiKey required');
    if (!opts.storesUrl && !opts.stores) throw new Error('StoreLocator: storesUrl or stores required');

    const root = typeof opts.container === 'string'
      ? document.querySelector(opts.container)
      : opts.container;
    if (!root) throw new Error('StoreLocator: container not found');

    const theme = Object.assign({}, DEFAULT_THEME, opts.theme || {});
    const defaultRadius = opts.defaultRadius != null ? opts.defaultRadius : 25;
    const defaultCenter = opts.defaultCenter || US_CENTER;
    const defaultZoom = opts.defaultZoom != null ? opts.defaultZoom : (opts.defaultCenter ? 10 : US_ZOOM);

    if (!root.id) root.id = 'sl-' + Math.random().toString(36).slice(2, 9);
    injectStyles(theme, root.id);
    root.innerHTML = '';

    const state = {
      stores: [],
      filtered: [],
      origin: null, // { lat, lng } user search center
      radius: defaultRadius,
      map: null,
      markers: [],
      cluster: null,
      info: null,
      activeId: null,
      originMarker: null,
    };

    // --- DOM scaffold ---
    const searchInput = el('input', {
      type: 'text',
      class: 'sl-search-input',
      placeholder: 'Enter address or zip',
      'aria-label': 'Search address or zip',
    });
    const clearBtn = el('button', { class: 'sl-clear-btn', 'aria-label': 'Clear' });
    clearBtn.innerHTML = ICON_CLEAR;
    clearBtn.style.display = 'none';
    const geoBtn = el('button', { class: 'sl-geo-btn', 'aria-label': 'Use my location' });
    geoBtn.innerHTML = ICON_GEO;
    const searchIcon = el('span', { class: 'sl-search-icon' });
    searchIcon.innerHTML = ICON_SEARCH;

    const searchWrap = el('div', { class: 'sl-search-wrap' }, [
      searchIcon, searchInput, clearBtn, geoBtn,
    ]);

    const distSelectEl = el('select');
    DISTANCE_OPTIONS.forEach((o) => {
      const opt = el('option', { value: String(o.value) }, o.label);
      if (o.value === defaultRadius) opt.selected = true;
      distSelectEl.appendChild(opt);
    });
    const distSelect = el('div', { class: 'sl-select' }, distSelectEl);

    const filters = el('div', { class: 'sl-filters' }, [distSelect]);
    const controls = el('div', { class: 'sl-controls' }, [searchWrap, filters]);

    const list = el('div', { class: 'sl-list' });
    list.appendChild(el('div', { class: 'sl-loading' }, 'Loading stores…'));
    const panel = el('div', { class: 'sl-panel' }, [controls, list]);

    const mapDiv = el('div', { class: 'sl-map' });
    root.appendChild(panel);
    root.appendChild(mapDiv);

    // --- input handlers ---
    searchInput.addEventListener('input', () => {
      clearBtn.style.display = searchInput.value ? 'flex' : 'none';
    });
    clearBtn.addEventListener('click', () => {
      searchInput.value = '';
      clearBtn.style.display = 'none';
      state.origin = null;
      if (state.originMarker) { state.originMarker.setMap(null); state.originMarker = null; }
      render();
      fitToVisible();
    });
    geoBtn.addEventListener('click', () => {
      if (!navigator.geolocation) return;
      geoBtn.disabled = true;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          geoBtn.disabled = false;
          state.origin = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          searchInput.value = 'Current location';
          clearBtn.style.display = 'flex';
          setOriginMarker();
          render();
          fitToVisible();
        },
        () => { geoBtn.disabled = false; },
        { enableHighAccuracy: false, timeout: 8000 }
      );
    });
    distSelectEl.addEventListener('change', () => {
      state.radius = Number(distSelectEl.value);
      render();
      fitToVisible();
    });

    // --- core loaders ---
    function loadStores() {
      if (opts.stores) return Promise.resolve(opts.stores);
      return fetch(opts.storesUrl, { credentials: 'omit' }).then((r) => {
        if (!r.ok) throw new Error('Stores fetch failed: ' + r.status);
        return r.json();
      });
    }

    function setOriginMarker() {
      if (!state.map || !state.origin) return;
      if (state.originMarker) state.originMarker.setMap(null);
      state.originMarker = new google.maps.Marker({
        position: state.origin,
        map: state.map,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: '#3b82f6',
          fillOpacity: 1,
          strokeColor: '#fff',
          strokeWeight: 3,
        },
        zIndex: 9999,
      });
    }

    // --- filtering / rendering ---
    function computeFiltered() {
      let arr = state.stores.slice();
      if (state.origin && state.radius > 0) {
        arr = arr
          .map((s) => Object.assign({}, s, { _distance: haversineMiles(state.origin, { lat: s.lat, lng: s.lng }) }))
          .filter((s) => s._distance <= state.radius);
      } else if (state.origin) {
        arr = arr.map((s) => Object.assign({}, s, { _distance: haversineMiles(state.origin, { lat: s.lat, lng: s.lng }) }));
      }
      if (state.origin) arr.sort((a, b) => a._distance - b._distance);
      else arr.sort((a, b) => a.name.localeCompare(b.name));
      return arr;
    }

    function render() {
      state.filtered = computeFiltered();
      state.fallback = false;
      // Nothing inside the radius → show the closest stores rather than nothing.
      if (!state.filtered.length && state.origin && state.radius > 0 && state.stores.length) {
        const n = opts.fallbackCount != null ? opts.fallbackCount : 5;
        if (n > 0) {
          state.filtered = state.stores
            .map((s) => Object.assign({}, s, { _distance: haversineMiles(state.origin, { lat: s.lat, lng: s.lng }) }))
            .sort((a, b) => a._distance - b._distance)
            .slice(0, n);
          state.fallback = true;
        }
      }
      renderList();
      renderMarkers();
    }

    function renderList() {
      list.innerHTML = '';
      if (!state.filtered.length) {
        list.appendChild(el('div', { class: 'sl-empty' }, 'No stores match your filters.'));
        return;
      }
      if (state.fallback) {
        list.appendChild(el('div', { class: 'sl-notice' },
          'No stores within ' + state.radius + ' miles yet. Here are the closest ones — or order online below.'));
      }
      state.filtered.forEach((s) => {
        const open = isOpenNow(s.hours);
        const meta = el('div', { class: 'sl-meta' });
        if (open !== null) {
          const badge = el('span', { class: 'sl-badge' + (open ? '' : ' sl-closed') }, [
            el('span', { class: 'sl-dot' }),
            open ? 'Open Now' : 'Closed',
          ]);
          meta.appendChild(badge);
        }
        if (s._distance != null) {
          meta.appendChild(
            el('span', { class: 'sl-distance' }, s._distance.toFixed(1) + ' mi')
          );
        }
        var dirUrl = 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(s.address || (s.lat + ',' + s.lng));
        var dirLink = el('a', {
          class: 'sl-directions',
          href: dirUrl,
          target: '_blank',
          rel: 'noopener',
          onclick: function(e) { e.stopPropagation(); }
        }, 'Get Directions →');
        const item = el('div', {
          class: 'sl-item' + (state.activeId === s.id ? ' active' : ''),
          onclick: () => focusStore(s),
        }, [
          el('h3', { class: 'sl-item-name' }, s.name),
          el('p', { class: 'sl-item-addr' }, s.address || ''),
          meta,
          dirLink,
        ]);
        list.appendChild(item);
      });
    }

    function renderMarkers() {
      if (!state.map) return;
      if (state.cluster) state.cluster.clearMarkers();
      state.markers.forEach((m) => m.setMap(null));
      state.markers = [];

      const visibleIds = new Set(state.filtered.map((s) => s.id));
      // Show all stores on the map but emphasize filtered ones.
      // For simplicity: only render filtered stores.
      state.filtered.forEach((s) => {
        const icon = resolveMarkerIcon(opts.markerIcon, s);
        const marker = new google.maps.Marker({
          position: { lat: s.lat, lng: s.lng },
          icon: {
            url: icon.url,
            scaledSize: new google.maps.Size(icon.size[0], icon.size[1]),
            anchor: new google.maps.Point(icon.anchor[0], icon.anchor[1]),
          },
          title: s.name,
        });
        marker.addListener('click', () => focusStore(s, true));
        state.markers.push(marker);
      });

      if (global.markerClusterer && state.markers.length) {
        state.cluster = new global.markerClusterer.MarkerClusterer({
          map: state.map,
          markers: state.markers,
        });
      } else {
        state.markers.forEach((m) => m.setMap(state.map));
      }
    }

    function fitToVisible() {
      if (!state.map || !state.markers.length) {
        if (state.map) {
          state.map.setCenter(defaultCenter);
          state.map.setZoom(defaultZoom);
        }
        return;
      }
      const bounds = new google.maps.LatLngBounds();
      state.markers.forEach((m) => bounds.extend(m.getPosition()));
      if (state.origin) bounds.extend(state.origin);
      state.map.fitBounds(bounds, 64);
      // Cap zoom so a single marker doesn't max out
      const listener = google.maps.event.addListenerOnce(state.map, 'idle', () => {
        if (state.map.getZoom() > 15) state.map.setZoom(15);
      });
    }

    function focusStore(s, fromMarker) {
      state.activeId = s.id;
      renderList();
      if (!state.map) return;
      state.map.panTo({ lat: s.lat, lng: s.lng });
      if (state.map.getZoom() < 13) state.map.setZoom(13);
      if (!state.info) state.info = new google.maps.InfoWindow();
      const open = isOpenNow(s.hours);
      const directionsUrl = 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(s.address || (s.lat + ',' + s.lng));
      const html =
        '<div class="sl-info">' +
        '<h4>' + escapeHtml(s.name) + '</h4>' +
        (s.address ? '<p>' + escapeHtml(s.address) + '</p>' : '') +
        (s.phone ? '<p><a href="tel:' + escapeAttr(s.phone) + '">' + escapeHtml(s.phone) + '</a></p>' : '') +
        '<p style="display:flex;gap:12px;align-items:center;flex-wrap:wrap;">' +
        '<a href="' + escapeAttr(directionsUrl) + '" target="_blank" rel="noopener" style="color:' + theme.primary + ';font-weight:600;text-decoration:none;">Get Directions &rarr;</a>' +
        (s.website ? '<a href="' + escapeAttr(s.website) + '" target="_blank" rel="noopener" style="color:' + theme.accent + ';text-decoration:none;">Website</a>' : '') +
        '</p>' +
        (open === true ? '<p><span class="sl-badge"><span class="sl-dot"></span>Open Now</span></p>' : open === false ? '<p><span class="sl-badge sl-closed"><span class="sl-dot"></span>Closed</span></p>' : '') +
        '</div>';
      state.info.setContent(html);
      const marker = state.markers.find((m) => {
        const p = m.getPosition();
        return p.lat() === s.lat && p.lng() === s.lng;
      });
      if (marker) state.info.open(state.map, marker);

      if (!fromMarker) {
        // scroll list item into view
        const items = list.querySelectorAll('.sl-item');  // .sl-notice is not an .sl-item, so indexes still line up
        const idx = state.filtered.findIndex((x) => x.id === s.id);
        if (items[idx]) items[idx].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }

    function escapeHtml(str) {
      return String(str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    }
    function escapeAttr(str) { return escapeHtml(str); }

    // --- boot ---
    Promise.all([
      loadGoogleMaps(opts.googleMapsApiKey),
      loadMarkerClusterer(),
      loadStores(),
    ])
      .then(([_, __, stores]) => {
        state.stores = (Array.isArray(stores) ? stores : stores.stores || [])
          .filter((s) => s && typeof s.lat === 'number' && typeof s.lng === 'number')
          .map((s, i) => Object.assign({ id: s.id || 'store-' + i, type: s.type || 'cash-carry' }, s));

        // map
        state.map = new google.maps.Map(mapDiv, {
          center: defaultCenter,
          zoom: defaultZoom,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          clickableIcons: false,
          styles: opts.mapStyles || [],
        });

        // Places autocomplete on the search input.
        // Wrapped in try-catch: google.maps.places.Autocomplete is deprecated
        // and unavailable for new API customers as of March 2025.  If it
        // throws, the widget still works via the Enter-key geocoder below.
        var _acAttached = false;
        try {
          if (google.maps.places && google.maps.places.Autocomplete) {
            const ac = new google.maps.places.Autocomplete(searchInput, {
              types: ['geocode'],
              fields: ['geometry', 'formatted_address'],
            });
            ac.addListener('place_changed', () => {
              const place = ac.getPlace();
              if (!place.geometry || !place.geometry.location) return;
              state.origin = {
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng(),
              };
              clearBtn.style.display = 'flex';
              setOriginMarker();
              render();
              fitToVisible();
            });
            _acAttached = true;
          }
        } catch (acErr) {
          console.warn('[StoreLocator] Places Autocomplete unavailable, using geocoder fallback:', acErr.message);
        }

        // Enter key → geocode. This is the primary search method when
        // Autocomplete is unavailable, and a fallback when it is.
        searchInput.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter') return;
          e.preventDefault(); // prevent form submission if wrapped in a form
          const q = searchInput.value.trim();
          if (!q) return;
          const delay = _acAttached ? 250 : 0;
          setTimeout(() => {
            // if autocomplete already populated origin in this tick, skip
            if (_acAttached && state._lastQuery === q) return;
            state._lastQuery = q;
            const geocoder = new google.maps.Geocoder();
            geocoder.geocode({ address: q }, (results, status) => {
              if (status === 'OK' && results[0]) {
                const loc = results[0].geometry.location;
                state.origin = { lat: loc.lat(), lng: loc.lng() };
                clearBtn.style.display = 'flex';
                setOriginMarker();
                render();
                fitToVisible();
              }
            });
          }, delay);
        });

        render();
        // Initial centering: defaultCenter (if provided) → otherwise fit to all stores.
        if (opts.defaultCenter) {
          state.map.setCenter(defaultCenter);
          state.map.setZoom(defaultZoom);
        } else {
          fitToVisible();
        }

        // Auto-locate: silently try the browser's geolocation API. If granted,
        // re-center on the user, set the origin, and resort by distance.
        // If denied or unavailable, we keep the default view — no UX hang.
        if (opts.autoLocate && navigator.geolocation) {
          const geoTimeoutMs = opts.autoLocateTimeoutMs || 8000;
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              state.origin = { lat: pos.coords.latitude, lng: pos.coords.longitude };
              searchInput.value = 'Current location';
              clearBtn.style.display = 'flex';
              setOriginMarker();
              render();
              fitToVisible();
            },
            (err) => {
              // Denied, unavailable, or timed out — silent fallback.
              if (opts.onGeolocationError) opts.onGeolocationError(err);
            },
            { enableHighAccuracy: false, timeout: geoTimeoutMs, maximumAge: 5 * 60 * 1000 }
          );
        }
      })
      .catch((err) => {
        console.error('[StoreLocator]', err);
        list.innerHTML = '';
        list.appendChild(el('div', { class: 'sl-error' }, 'Could not load store locator: ' + err.message));
      });

    return {
      destroy() {
        if (state.cluster) state.cluster.clearMarkers();
        state.markers.forEach((m) => m.setMap(null));
        root.innerHTML = '';
      },
    };
  }

  global.StoreLocator = { init };
})(typeof window !== 'undefined' ? window : globalThis);
